<template>
  <div class="container-fluid">
    <div class="row">
      <div class="col-3 sidebar-org bg-custom-blue py-3">

        <!-- Header left sidebar -->
        <div class="d-flex justify-content-between align-items-center mb-2">
          <h4 class="mb-0">{{ $t("views.nav.manageVisions") }}</h4>
        </div>

        <!-- Time-Filter -->
        <div class="row mt-4">
          <div class="col-6">
            <button class="btn btn-sm dropdown-toggle bg-white text-start" style="width: 200px" type="button" data-bs-toggle="dropdown" aria-expanded="false">
              <span>{{ formatDate(new Date(filterValidFrom)) }} &nbsp;-&nbsp; {{ formatDate(new Date(filterValidTo)) }}</span>
            </button>
            <div class="dropdown-menu border-secondary p-3">
              <div class="row">
                <div class="col-12">
                  <button @click="filterOrgUnits(new Date().toISOString().slice(0, 10), new Date().toISOString().slice(0, 10))" class="btn btn-sm btn-outline-primary me-2 mb-2" type="button">{{ $t("general.today") }}</button>
                  <button @click="filterOrgUnits((new Date().getFullYear() + 1).toString() + '-01-01', (new Date().getFullYear() + 1).toString() + '-12-31')" class="btn btn-sm btn-outline-primary me-2 mb-2" type="button">{{ new Date().getFullYear() + 1 }}</button>
                  <button @click="filterOrgUnits(new Date().getFullYear().toString() + '-01-01', new Date().getFullYear().toString() + '-12-31')" class="btn btn-sm btn-outline-primary me-2 mb-2" type="button">{{ new Date().getFullYear() }}</button>
                  <button @click="filterOrgUnits((new Date().getFullYear() - 1).toString() + '-01-01', (new Date().getFullYear() - 1).toString() + '-12-31')" class="btn btn-sm btn-outline-primary me-2 mb-2" type="button">{{ new Date().getFullYear() - 1 }}</button>
                </div>
              </div>
              <hr class="dropdown-divider">
              <div class="row">
                <div class="col-6">
                  <label for="filterValidFrom" class="form-label">{{ $t("views.kpi.periods.from") }}:</label>
                  <input v-model="filterValidFrom" type="date" class="form-control form-control-sm" id="filterValidFrom">
                </div>
                <div class="col-6">
                  <label for="filterValidTo" class="form-label">{{ $t("views.kpi.periods.to") }}:</label>
                  <input v-model="filterValidTo" type="date" class="form-control form-control-sm" id="filterValidTo">
                </div>
              </div>
              <div class="row">
                <div class="col">
                  <button @click="filterOrgUnits(filterValidFrom, filterValidTo)" class="btn btn-sm btn-outline-primary mt-3" type="button">{{ $t("views.kpi.periods.filter") }}</button>
                </div>
              </div>
            </div>
          </div>
          <div class="col-6 text-end">
            <select v-model="selectedTree" id="selectedTree" class="form-select form-select-sm dropdown-toggle text-start">
              <option value="complete">{{ $t("views.strategy.structure.completeTree") }}</option>
              <option value="vision">{{ $t("views.strategy.structure.visionEtc") }}</option>
            </select>
          </div>
        </div>

        <!-- Search & Slider -->
        <div class="row mt-1">
          <div class="col-12">
            <div class="d-flex justify-content-between align-items-center">
              <!-- Search  -->
              <div class="me-3" style="width: 200px"> <input v-model="searchText" type="text" :placeholder="$t('general.placeholderSearch')" class="form-control form-control-sm mt-1" />
              </div>
              <!-- Level-Slider from 2 to maxTreeLevel -->
              <div class="d-flex flex-column align-items-start mb-2">
                <div class="fs-08 text-muted mb-1"> {{ $t('general.treeViewLevels') }}: {{ autoOpenDepth }} </div>
                <div class="d-flex align-items-center gap-2">
                  <input type="range" class="form-range foc-range" min="2" :max="maxTreeDepth" step="1" v-model="autoOpenDepth" :key="sliderKey">
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="row">
          <div class="col-12">
            <OrgUnitTree v-if="'nodes' in orgUnits && orgUnits.nodes.length" :node="orgUnits" :depth="0" mode="manage" :searchText="searchText" :selectedTree="selectedTree" :autoOpenDepth="autoOpenDepth" class="mt-2"></OrgUnitTree>
          </div>
        </div>
      </div>
      <div class="col-9 px-5">
        <div v-if="Object.keys(selectedOrgUnit).length" class="row">
          <div class="col-12">
            <div class="row mt-3">
              <div class="col">
                <button v-if="selectedOrgUnit.write" @click="openModal(selectedOrgUnit.id, 'orgUnit', 'update')" type="button" class="btn btn-sm btn-outline-dark" data-bs-toggle="modal" data-bs-target="#orgUnitEditModal">&bull; {{ $t("general.update") }}</button>
              </div>
            </div>
            <div class="row mt-3">
              <div class="col">
                <div class="h3">{{ selectedOrgUnit.name }}</div>
                <div>
                  {{ $t("views.org.structure.validityPeriod") }}:
                  <span v-if="selectedOrgUnit.validFrom">
                    {{ formatDate(new Date(selectedOrgUnit.validFrom)) }} {{ $t("general.until") }} {{ formatDate(new Date(selectedOrgUnit.validTo)) }}
                  </span>
                  <span v-else class="fst-italic">
                    {{ $t("views.org.structure.infinite") }}
                  </span>
                </div>
              </div>
            </div>
            <div class="row mt-5">
              <div class="col-6">
                <div class="card mb-3" style="min-height: 16rem">
                  <div class="card-header bg-primary text-light">{{ $t("views.org.detail.purpose") }}</div>
                  <div class="card-body">
                    <div v-if="selectedOrgUnit.purpose">
                      <QuillContent :content="selectedOrgUnit.purpose" />
                    </div>
                    <div v-else class="card-text">{{ $t("views.org.detail.noPurpose") }}</div>
                  </div>
                </div>
              </div>
              <div class="col-6">
                <div class="card mb-3" style="min-height: 16rem">
                  <div class="card-header bg-primary text-light">{{ $t("views.org.detail.vision") }}</div>
                  <div class="card-body">
                    <div v-if="selectedOrgUnit.vision">
                      <QuillContent :content="selectedOrgUnit.vision" />
                    </div>
                    <div v-else class="card-text">{{ $t("views.org.detail.noVision") }}</div>
                  </div>
                </div>
              </div>
            </div>
            <div class="row mt-5">
              <div class="col-6">
                <div class="card mb-3" style="min-height: 16rem">
                  <div class="card-header bg-primary text-light">{{ $t("views.org.detail.mission") }}</div>
                  <div class="card-body">
                    <div v-if="selectedOrgUnit.mission">
                      <QuillContent :content="selectedOrgUnit.mission" />
                    </div>
                    <div v-else class="card-text">{{ $t("views.org.detail.noMission") }}</div>
                  </div>
                </div>
              </div>
              <div class="col-6">
                <div class="card mb-3" style="min-height: 16rem">
                  <div class="card-header bg-primary text-light">{{ $t("views.org.detail.value") }}</div>
                  <div class="card-body">
                    <div v-if="selectedOrgUnit.corporateValue">
                      <QuillContent :content="selectedOrgUnit.corporateValue" />
                    </div>
                    <div v-else class="card-text">{{ $t("views.org.detail.noValue") }}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div v-else class="text-center mt-5">
          <img src="@/assets/img/no_data.svg" class="img-fluid" width="250">
          <p v-if="'nodes' in orgUnits && orgUnits.nodes.length" class="h5 mt-3">{{ $t("views.org.structure.helpChooseOrgUnit") }}</p>
          <p v-if="isAdmin && this.$store.state.selectedView === 'org'" class="h5 mt-3"> {{ $t("views.org.structure.helpCreateOrgUnit") }}</p>
        </div>
      </div>
    </div>
    <OrgUnitEditModal></OrgUnitEditModal>
    <OrgUnitDeleteModal></OrgUnitDeleteModal>
  </div>
</template>

<script>
import OrgUnitTree from '@/components/org/OrgUnitTree.vue'
import OrgUnitEditModal from '@/components/org/OrgUnitEditModal.vue'
import OrgUnitDeleteModal from '@/components/org/OrgUnitDeleteModal.vue'
import QuillContent from '@/components/QuillContent.vue'
import * as formatUtils from '@/composables/formatUtils'
import * as tree from '@/composables/tree'

export default {
  name: 'OrgManageOrgUnit',
  data () {
    return {
      orgUnitModalElementId: false,
      orgUnitModalElementType: '',
      orgUnitModalMode: '',
      selectedElement: '',
      filterValidFrom: this.$store.state.user?.settings?.favorites?.orgUnitManage?.filterValidFrom ? this.$store.state.user.settings.favorites.orgUnitManage.filterValidFrom : new Date().getFullYear().toString() + '-01-01',
      filterValidTo: this.$store.state.user?.settings?.favorites?.orgUnitManage?.filterValidTo ? this.$store.state.user.settings.favorites.orgUnitManage.filterValidTo : (new Date().getFullYear() + 1).toString() + '-12-31',
      searchText: '',
      selectedTree: '',
      autoOpenDepth: 2,
      sliderKey: 0
    }
  },
  components: {
    OrgUnitTree,
    OrgUnitEditModal,
    OrgUnitDeleteModal,
    QuillContent
  },
  computed: {
    isAdmin () {
      return this.$store.state.user.role === 10387
    },
    sysParms () {
      return this.$store.state.sysParms
    },
    orgUnits () {
      return this.$store.state.orgUnits
    },
    selectedOrgUnit () {
      return this.$store.state.selectedOrgUnit
    },
    selectedView () {
      return this.$store.state.selectedView
    },

    // maximale Strukturtiefe ermitteln (Fallback 2)
    maxTreeDepth () {
      return tree.getMaxTreeDepth(this.$store.state.orgUnits?.nodes, 2)
    }
  },

  watch: {
    'orgUnits.nodes': {
      handler (nodes) {
        if (!nodes || nodes.length === 0) return

        // Set Slider Default-Level (Fallback = 2)
        const depth = this.maxTreeDepth
        const defaultLevel = this.sysParms?.orgTreeDefaultLevel ?? 2
        this.autoOpenDepth = Math.min(Math.max(defaultLevel, 2), depth)

        // Render Slider
        this.sliderKey++
      },
      immediate: true
    }
  },

  methods: {
    async filterOrgUnits (filterFrom, filterTo) {
      this.$store.commit('SET_LOADING_STATUS', true)
      this.filterValidFrom = filterFrom
      this.filterValidTo = filterTo
      await this.$store.dispatch('getOrgUnits', { validFrom: filterFrom, validTo: filterTo, view: 'vision' })
      await this.$store.dispatch('favoriteObject', { isList: false, objectKey: 'orgUnitManage', objectValue: { filterValidFrom: filterFrom, filterValidTo: filterTo } })
      this.$store.commit('SET_LOADING_STATUS', false)
    },
    openModal (id, elementType, mode) {
      if (id) {
        this.orgUnitModalElementId = id
      }
      this.orgUnitModalElementType = elementType
      this.orgUnitModalMode = mode
    },
    formatDate (date) {
      const userLocale = this.$store.state.user.settings?.language
      return formatUtils.formatDate(userLocale, date)
    }
  },
  async created () {
    this.selectedTree = 'vision'
    this.$store.commit('SET_LOADING_STATUS', true)
    this.$store.commit('SET_SELECTED_VIEW', 'vision')
    await this.$store.dispatch('getOrgUnits', { validFrom: this.filterValidFrom, validTo: this.filterValidTo, view: 'vision' })

    if (Object.keys(this.selectedOrgUnit).length) {
      await this.$store.dispatch('getOrgUnit', this.selectedOrgUnit.id)
    }
    this.$store.commit('SET_LOADING_STATUS', false)
  }
}
</script>
